/**
 * Thin wrapper around the browser's speech recognition.
 *
 * Recognition is handled by the device's own dictation service, which generally sends audio
 * away to be transcribed and does not work offline. Nothing else in TED leaves the device,
 * so this is kept in one small file, is entirely optional, and the interface says plainly
 * what it does before it is used.
 */

import type { LabelProposal } from "./scan";

type RecognitionEvent = { results?: ArrayLike<ArrayLike<{ transcript?: string }>> };
type RecognitionErrorEvent = { error?: string };
type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  /** Fires once the microphone is actually open. Optional: not every engine emits it. */
  onaudiostart: (() => void) | null;
};

type RecognitionConstructor = new () => Recognition;

const recognitionConstructor = (): RecognitionConstructor | undefined => {
  if (typeof window === "undefined") return undefined;
  const scope = window as unknown as { SpeechRecognition?: RecognitionConstructor; webkitSpeechRecognition?: RecognitionConstructor };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition;
};

export const speechRecognitionAvailable = () => recognitionConstructor() !== undefined;

/**
 * How long to wait for any sign that the recogniser is running. `start()` returning without
 * throwing does not mean it started: on at least one iPhone it opened nothing, raised no
 * error and never ended, so "Listening…" sat there for as long as anyone was willing to
 * watch it. Six seconds is far longer than a microphone takes to open.
 */
const AUDIO_START_TIMEOUT_MS = 6_000;

/**
 * The backstop once the microphone genuinely is open. A short phrase is transcribed in a
 * couple of seconds and the engine's own silence cutoff arrives well before this; this is
 * only here so no path can wait forever.
 */
const LISTENING_TIMEOUT_MS = 20_000;

/** Plain messages. The user is told what to do next, never given an error code. */
export function speechErrorMessage(error?: string) {
  if (error === "not-allowed" || error === "service-not-allowed") return "TED does not have permission to use the microphone. You can allow it in Settings, or type the dose instead.";
  if (error === "no-speech") return "Nothing was heard that time.";
  if (error === "audio-capture") return "No microphone was available on this device.";
  if (error === "network") return "Speech needs a connection and this device is offline. Type the dose instead.";
  // Not codes the browser raises — TED's own, for the two ways it can be left waiting.
  if (error === "did-not-start") return "Listening did not start on this device. Dictation may be switched off: you can check in Settings, under General, then Keyboard. You can type the dose instead.";
  if (error === "no-response") return "The microphone opened but nothing came back. Try again, or type the dose instead.";
  return "Speech did not work that time. Try again, or type the dose instead.";
}

/* --------------------------------------------------------------------------------------
 * Reading medicine details out of a spoken phrase.
 *
 * The same rule as label scanning applies, and matters more here because speech is looser:
 * TED proposes only what was actually said. It never supplies a usual strength, a typical
 * amount or a likely form, and it knows nothing about any medicine. Every proposal carries
 * the phrase it came from, and none is applied until it is approved.
 *
 * The proposals deliberately share the shape the camera produces, so both fill the form
 * through one approval list and one apply path rather than two that can drift apart.
 * ----------------------------------------------------------------------------------- */

const SPOKEN_NUMBERS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8,
  nine: 9, ten: 10, eleven: 11, twelve: 12,
  a: 1, an: 1, once: 1, twice: 2,
};

/**
 * Numbers as they are spoken.
 *
 * Dictation writes small numbers as words at least as often as digits, and a strength that
 * failed to parse used to fall through into the medicine's name — "amitriptyline ten
 * milligrams" became a medicine of that name.
 */
const SMALL_WORDS: Record<string, number> = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9,
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16,
  seventeen: 17, eighteen: 18, nineteen: 19,
};
const TENS_WORDS: Record<string, number> = {
  twenty: 20, thirty: 30, forty: 40, fourty: 40, fifty: 50,
  sixty: 60, seventy: 70, eighty: 80, ninety: 90,
};
const NUMBER_WORD_SOURCE = [...Object.keys(SMALL_WORDS), ...Object.keys(TENS_WORDS), "hundred", "thousand", "and", "an", "a"].join("|");

/**
 * Read a spoken quantity, or nothing at all.
 *
 * This writes a medication strength, so the grammar is strict on purpose: a run of words
 * that does not form one well-shaped number returns undefined rather than a best guess.
 * Anything unparsed stays in the phrase and surfaces in the name, where it is visible and
 * can be corrected — which is much safer than quietly proposing the wrong milligrams.
 */
export function spokenQuantity(text: string): number | undefined {
  const raw = text.trim().toLowerCase().replace(",", ".");
  if (/^\d+(?:\.\d+)?$/.test(raw)) return Number(raw);

  const words = raw.split(/[\s-]+/).filter(Boolean);
  // "a" alone is an article far more often than it is the number one.
  if (words.length === 0 || words.every((word) => word === "and" || word === "a" || word === "an")) return undefined;

  let total = 0;
  let current = 0;
  // What may legitimately follow what. "two hundred and fifty" is a number; "fifty sixty"
  // and "five six" are two numbers said in a row, and are refused rather than added up.
  let state: "start" | "afterSmall" | "afterTens" | "afterHundred" = "start";
  for (const word of words) {
    if (word === "and") continue;

    if (word === "a" || word === "an") {
      if (state !== "start") return undefined;
      current = 1;
      state = "afterSmall";
      continue;
    }

    if (word === "hundred") {
      // Something must be in front of it, and it must be a plain count: "twenty hundred" is
      // not a number, "two hundred" is.
      if (state !== "afterSmall" || current === 0) return undefined;
      current *= 100;
      state = "afterHundred";
      continue;
    }
    if (word === "thousand") {
      if (current === 0) return undefined;
      total += current * 1000;
      current = 0;
      state = "start";
      continue;
    }

    const small = SMALL_WORDS[word];
    if (small !== undefined) {
      if (state === "afterSmall") return undefined;
      // After a tens word only a single digit may follow: "twenty fifteen" is not 35.
      if (state === "afterTens" && small > 9) return undefined;
      current += small;
      state = "afterSmall";
      continue;
    }

    const tens = TENS_WORDS[word];
    if (tens !== undefined) {
      if (state !== "start" && state !== "afterHundred") return undefined;
      current += tens;
      state = "afterTens";
      continue;
    }
    return undefined;
  }
  const value = total + current;
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

/** Dictation writes units out in full at least as often as it abbreviates them. */
const STRENGTH_UNITS = "micrograms?|microgram|mcg|milligrams?|milligram|mgs?|grams?|gram|g|millilitres?|millilitre|mls?|ml|units?";
const SPOKEN_STRENGTH = new RegExp(`(\\d+(?:[.,]\\d+)?|(?:${NUMBER_WORD_SOURCE})(?:[\\s-]+(?:${NUMBER_WORD_SOURCE}))*)\\s*(${STRENGTH_UNITS})\\b`, "i");

/**
 * How often, said out loud. Not proposed as a schedule — no one speaks the clock times, and
 * TED will not invent them — but it must not be left to wash up in the medicine's name.
 */
const SPOKEN_FREQUENCY = new RegExp(
  `\\b(?:(?:once|twice|thrice|\\d+|${NUMBER_WORD_SOURCE})\\s*(?:times?\\s*)?(?:a|per|each)\\s*day` +
  `|(?:once|twice|thrice|\\d+|${NUMBER_WORD_SOURCE})\\s*times?\\s*daily` +
  `|(?:once|twice|thrice)\\s*daily|daily` +
  `|every\\s+(?:\\d+|${NUMBER_WORD_SOURCE})\\s*(?:hours?|hrs?)` +
  `|at night|at bedtime|before bed|in the (?:morning|afternoon|evening)|with (?:food|meals?))\\b`,
  "gi",
);
const SPOKEN_FORMS: [RegExp, string][] = [
  [/\btablets?\b/i, "tablet"], [/\bcaps(ules?)?\b/i, "capsule"],
  [/\binjections?\b/i, "injection"], [/\b(liquid|suspension|solution|syrup)\b/i, "liquid"],
  [/\bpatch(es)?\b/i, "patch"], [/\b(inhalers?|puffs?)\b/i, "inhaler"],
  [/\bsprays?\b/i, "spray"], [/\bdrops?\b/i, "drops"],
  [/\bsuppositor(y|ies)\b/i, "suppository"], [/\b(cream|ointment|gel)\b/i, "cream-ointment"],
];
/** Words that carry no medicine detail and only get in the way of the name. */
const FILLER = /^(?:add|it'?s|its|this is|the|a|an|of|called|is|and|with|take|taking|i take)\b/i;

const spokenUnit = (unit: string) => {
  const lower = unit.toLowerCase();
  if (lower.startsWith("microgram") || lower === "mcg") return "mcg";
  if (lower.startsWith("milligram") || lower === "mg" || lower === "mgs") return "mg";
  if (lower.startsWith("millilitre") || lower === "ml" || lower === "mls") return "mL";
  if (lower.startsWith("gram") || lower === "g") return "g";
  return "units";
};

const spokenCount = (token: string) => {
  const cleaned = token.replace(/[^\w.]/g, "").toLowerCase();
  if (/^\d+(?:\.\d+)?$/.test(cleaned)) return Number(cleaned);
  return SPOKEN_NUMBERS[cleaned];
};

/**
 * Read a spoken phrase such as "amitriptyline 10 milligrams, two tablets" into proposals
 * for the medicine form. Anything the phrase does not carry is simply not proposed.
 */
export function interpretMedicineSpeech(transcript: string): LabelProposal[] {
  const phrase = transcript.replace(/\s+/g, " ").trim();
  if (!phrase) return [];
  const proposals: LabelProposal[] = [];

  // Everything read out of the phrase is recorded as a span, so the name can be built by
  // removing exactly what was used and nothing else. A number left in the phrase — the 30
  // and 500 in "co-codamol 30 500" — is part of the name and must survive.
  const consumed: [number, number][] = [];

  // A quantity that does not parse cleanly is left alone rather than guessed at, so the
  // words stay in the name where they can be seen and corrected.
  for (const match of phrase.matchAll(new RegExp(SPOKEN_STRENGTH.source, "gi"))) {
    const value = spokenQuantity(match[1]);
    if (match.index === undefined || value === undefined) continue;
    consumed.push([match.index, match.index + match[0].length]);
    if (proposals.some((item) => item.field === "strength")) continue;
    const unit = spokenUnit(match[2]);
    proposals.push({
      field: "strength", label: "Strength", display: `${value} ${unit}`,
      source: phrase, value: { strength: String(value), strengthUnit: unit },
    });
  }

  // How often was said out loud but cannot become a schedule, since no one speaks the clock
  // times. It is taken out of the phrase so it does not end up in the medicine's name.
  for (const match of phrase.matchAll(SPOKEN_FREQUENCY)) {
    if (match.index !== undefined) consumed.push([match.index, match.index + match[0].length]);
  }

  // A count is only read where it sits directly against a form word ("two tablets"), so a
  // strength said on its own is never mistaken for how many to take: in "10 milligram
  // tablets" the word before the form is "milligram", which is not a number at all.
  for (const [pattern, formName] of SPOKEN_FORMS) {
    const found = phrase.match(pattern);
    if (!found || found.index === undefined) continue;
    consumed.push([found.index, found.index + found[0].length]);
    proposals.push({
      field: "form", label: "Form",
      display: formName === "cream-ointment" ? "Cream / ointment" : formName.charAt(0).toUpperCase() + formName.slice(1),
      source: phrase, value: { form: formName },
    });

    const preceding = phrase.slice(0, found.index).match(/(\S+)\s*$/);
    const counted = preceding ? spokenCount(preceding[1]) : undefined;
    if (preceding?.index !== undefined && counted !== undefined && counted > 0) {
      consumed.push([preceding.index, preceding.index + preceding[1].length]);
      proposals.push({ field: "amount", label: "Amount each time", display: String(counted), source: phrase, value: { amount: String(counted) } });
    }
    break;
  }

  // The name is whatever is left once those spans are taken out. Nothing is added to it.
  let name = "";
  let cursor = 0;
  for (const [start, end] of consumed.sort((a, b) => a[0] - b[0])) {
    if (start > cursor) name += phrase.slice(cursor, start);
    cursor = Math.max(cursor, end);
  }
  name += phrase.slice(cursor);
  name = name.replace(/\s{2,}/g, " ").trim();
  while (FILLER.test(name)) name = name.replace(FILLER, "").trim();
  name = name.replace(/^[,.;:\-\s]+|[,.;:\-\s]+$/g, "").replace(/\s{2,}/g, " ").trim();
  if (name.length >= 2) {
    proposals.push({ field: "name", label: "Medicine name", display: name, source: phrase, value: { name } });
  }

  const order = ["name", "strength", "form", "amount"];
  return proposals.sort((a, b) => order.indexOf(a.field) - order.indexOf(b.field));
}

export type SpeechSession = { stop: () => void };

export function startDictation(handlers: {
  onResult: (transcript: string) => void;
  onError: (message: string) => void;
}): SpeechSession | null {
  const Constructor = recognitionConstructor();
  if (!Constructor) {
    // Returning null silently left the caller's sheet saying "Listening…" with nothing on the
    // way. Both callers gate on speechRecognitionAvailable(), so this is unreachable today —
    // it is still the same defect, and one line.
    handlers.onError(speechErrorMessage());
    return null;
  }

  let settled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const recognition = new Constructor();
  recognition.lang = "en-GB";
  recognition.continuous = false;
  recognition.interimResults = false;
  recognition.maxAlternatives = 1;

  const clear = () => { if (timer !== undefined) { clearTimeout(timer); timer = undefined; } };
  const waitFor = (ms: number, reason: string) => {
    clear();
    timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      try { recognition.abort(); } catch { /* nothing to abort */ }
      handlers.onError(speechErrorMessage(reason));
    }, ms);
  };

  recognition.onresult = (event) => {
    const transcript = event.results?.[0]?.[0]?.transcript ?? "";
    if (!transcript.trim()) return;
    settled = true;
    clear();
    handlers.onResult(transcript);
  };
  recognition.onerror = (event) => {
    clear();
    if (settled) return;
    settled = true;
    handlers.onError(speechErrorMessage(event.error));
  };
  // Recognition also ends when the user simply says nothing, with no error raised.
  recognition.onend = () => {
    clear();
    if (settled) return;
    settled = true;
    handlers.onError(speechErrorMessage("no-speech"));
  };
  // The microphone is open, so the engine is alive; wait the longer of the two.
  recognition.onaudiostart = () => { if (!settled) waitFor(LISTENING_TIMEOUT_MS, "no-response"); };

  try {
    recognition.start();
    waitFor(AUDIO_START_TIMEOUT_MS, "did-not-start");
  } catch {
    settled = true;
    clear();
    handlers.onError(speechErrorMessage());
    return null;
  }
  return { stop: () => { settled = true; clear(); try { recognition.abort(); } catch { /* already stopped */ } } };
}
